import type { Token } from '@kodiak-finance/sdk-core'
import REWARD_VAULT_ABI from 'abis/RewardVault.json'
import Loader from 'components/Loader'
import { DEFAULT_WAGMI_REFETCH_POLICY } from 'config/RefetchPolicies'
import { useAllBaults, useBaultCompounds } from 'hooks/backend/baults'
import { useIslandsQuery } from 'hooks/backend/islands'
import { useToken } from 'hooks/Tokens'
import { useBaultCompletionMetrics } from 'hooks/useBaultCompletionMetrics'
import { useBaultEarnedValues } from 'hooks/useBaultEarnedValues'
import { useConnection } from 'hooks/useConnection'
import { useGetTokenPrice, useGetTokenPrices } from 'hooks/useTokenPrice'
import BaultView from 'pages/Islands/IslandPage/IslandAnalyticsCard/BaultView'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Edit3, ExternalLink, Search } from 'react-feather'
import styled from 'styled-components'
import { StyledInternalLink } from 'theme'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'
import type { Abi, Address } from 'viem'
import { useReadContract } from 'wagmi'

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 10%;
  max-width: 100%;
  margin: 0 auto;
  min-height: 100vh;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
  text-align: center;
`

const TotalTVLContainer = styled.div`
  background: ${({ theme }) => theme.deprecated_bg2};
  border: 1px solid ${({ theme }) => theme.deprecated_primary1};
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  margin-bottom: 20px;
`

const TotalTVLLabel = styled.div`
  color: ${({ theme }) => theme.deprecated_text2};
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`

const TotalTVLValue = styled.div`
  color: ${({ theme }) => theme.deprecated_primary1};
  font-size: 32px;
  font-weight: 700;
`

const LossMakingBadge = styled.div`
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid #ff4444;
  border-radius: 20px;
  padding: 8px 16px;
  color: #ff4444;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: help;
  position: relative;

  &:hover::after {
    content: 'These baults are paying out more in earned rewards than they receive in bounties - losing money for the protocol';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: #333;
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    margin-bottom: 5px;
    max-width: 300px;
    white-space: normal;
    width: 280px;
  }

  &:hover::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #333;
    margin-bottom: -5px;
    z-index: 1000;
  }
`

const ControlsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;

  @media (max-width: 768px) {
    min-width: unset;
    width: 100%;
  }
`

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1px solid ${({ theme }) => theme.deprecated_bg3};
  border-radius: 8px;
  background: ${({ theme }) => theme.deprecated_bg1};
  color: ${({ theme }) => theme.deprecated_text1};
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.deprecated_primary1};
  }

  &::placeholder {
    color: ${({ theme }) => theme.deprecated_text2};
  }
`

const SearchIcon = styled(Search)`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.deprecated_text2};
  pointer-events: none;
`

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SortLabel = styled.span`
  color: ${({ theme }) => theme.deprecated_text2};
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`

const SortSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.deprecated_bg3};
  border-radius: 6px;
  background: ${({ theme }) => theme.deprecated_bg1};
  color: ${({ theme }) => theme.deprecated_text1};
  font-size: 14px;
  outline: none;
  min-width: 180px;

  &:focus {
    border-color: ${({ theme }) => theme.deprecated_primary1};
  }

  @media (max-width: 768px) {
    min-width: unset;
    width: 100%;
  }
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.deprecated_text1};
`

const Subtitle = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.deprecated_text2};
`

const BaultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

const BaultCard = styled.div<{ isExpanded: boolean; isLossMaking?: boolean }>`
  background: ${({ theme }) => theme.deprecated_bg2};
  border: 1px solid ${({ theme, isLossMaking }) => (isLossMaking ? '#ff4444' : theme.deprecated_bg3)};
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;
  width: 100%;
  ${({ isExpanded, theme }) => isExpanded && `border-color: ${theme.deprecated_primary1};`}
  ${({ isLossMaking }) => isLossMaking && `background: rgba(255, 68, 68, 0.05);`}

  &:hover {
    border-color: ${({ theme }) => theme.deprecated_primary1};
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }
`

const CardHeader = styled.div`
  padding: 20px;
  cursor: pointer;
  background: ${({ theme }) => theme.deprecated_bg1};

  &:hover {
    background: ${({ theme }) => theme.deprecated_bg2};
  }
`

const BaultInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const BaultMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const BaultName = styled.h3`
  color: ${({ theme }) => theme.deprecated_text1};
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`

const InactiveBadge = styled.span`
  margin-left: 8px;
  padding: 2px 8px;
  border: 1px solid ${({ theme }) => theme.deprecated_bg3};
  border-radius: 10px;
  color: ${({ theme }) => theme.deprecated_text2};
  font-size: 12px;
  font-weight: 500;
  vertical-align: middle;
`

const BaultAddress = styled.div`
  color: ${({ theme }) => theme.deprecated_text2};
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 11px;
  background: ${({ theme }) => theme.deprecated_bg3};
  padding: 3px 6px;
  border-radius: 4px;
  width: fit-content;
`

const ExpandToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.deprecated_text2};
  font-size: 13px;
  font-weight: 500;

  svg {
    width: 14px;
    height: 14px;
  }
`

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  gap: 12px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(6, 1fr);
  }

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
`

const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  background: ${({ theme }) => theme.deprecated_bg3};
  border-radius: 8px;
`

const MetricLabel = styled.div`
  color: ${({ theme }) => theme.deprecated_text2};
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const MetricValue = styled.div`
  color: ${({ theme }) => theme.deprecated_text1};
  font-size: 14px;
  font-weight: 600;
`

const ExpandedContent = styled.div`
  border-top: 1px solid ${({ theme }) => theme.deprecated_bg3};
  background: ${({ theme }) => theme.deprecated_bg1};
`

const ExpandedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.deprecated_bg3};
`

const ExpandedTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.deprecated_text1};
`

const ViewDetailsButton = styled.button`
  background: ${({ theme }) => theme.deprecated_primary1};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.deprecated_primary2};
    transform: translateY(-1px);
  }
`

const CompoundTable = styled.div`
  padding: 0 20px 20px 20px;
`

const CompoundRow = styled.div<{ isHeader?: boolean }>`
  display: grid;
  grid-template-columns: 120px 150px 120px 120px 120px 80px;
  gap: 12px;
  padding: 10px 0;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.deprecated_bg3};

  ${({ isHeader, theme }) =>
    isHeader &&
    `
    background: ${theme.deprecated_bg2};
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${theme.deprecated_text2};
    margin: 0 -10px;
    padding: 10px;
    border-radius: 6px;
  `}

  &:last-child {
    border-bottom: none;
  }
`

const CompoundCell = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.deprecated_text1};

  &.timestamp {
    color: ${({ theme }) => theme.deprecated_text2};
  }

  &.address {
    font-family: 'Monaco', 'Menlo', monospace;
    font-size: 11px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &.value {
    font-weight: 600;
  }

  &.usd {
    color: ${({ theme }) => theme.deprecated_text2};
    font-size: 11px;
  }

  &.leaked {
    color: ${({ theme }) => theme.deprecated_red1};
    font-weight: 600;
  }
  &.gained {
    color: ${({ theme }) => theme.deprecated_green1};
    font-weight: 600;
  }
`

const EditIcon = styled(Edit3)`
  width: 12px;
  height: 12px;
  cursor: pointer;
  color: ${({ theme }) => theme.deprecated_text2};

  &:hover {
    color: ${({ theme }) => theme.deprecated_primary1};
  }
`

const TxLink = styled.a`
  color: ${({ theme }) => theme.deprecated_primary1};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.deprecated_bg3};
  }

  svg {
    width: 12px;
    height: 12px;
  }
`

const BackButton = styled.button`
  background: ${({ theme }) => theme.deprecated_bg2};
  border: 1px solid ${({ theme }) => theme.deprecated_bg3};
  border-radius: 6px;
  color: ${({ theme }) => theme.deprecated_text1};
  cursor: pointer;
  font-size: 13px;
  padding: 8px 16px;
  transition: all 0.2s ease;
  width: fit-content;
  margin-bottom: 20px;

  &:hover {
    background: ${({ theme }) => theme.deprecated_bg3};
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
`

const ErrorContainer = styled.div`
  text-align: center;
  padding: 60px;
  color: ${({ theme }) => theme.deprecated_text2};
`

const AddressEditModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${({ theme }) => theme.deprecated_bg1};
  border: 1px solid ${({ theme }) => theme.deprecated_bg3};
  border-radius: 8px;
  padding: 20px;
  z-index: 1000;
  min-width: 300px;
`

const AddressEditOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`

const AddressEditInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.deprecated_bg3};
  border-radius: 4px;
  background: ${({ theme }) => theme.deprecated_bg2};
  color: ${({ theme }) => theme.deprecated_text1};
  margin: 10px 0;
`

const AddressEditButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 10px;
`

const AddressEditButton = styled.button<{ primary?: boolean }>`
  background: ${({ theme, primary }) => (primary ? theme.deprecated_primary1 : theme.deprecated_bg3)};
  color: ${({ theme, primary }) => (primary ? 'white' : theme.deprecated_text1)};
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const formatPercentage = (value: string | number): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '0%'
  return `${num.toFixed(2)}%`
}

const formatTokenAmount = (amount: string, decimals: number): string => {
  const num = parseFloat(amount) / Math.pow(10, decimals)
  if (isNaN(num)) return '0'
  if (num >= 1e6) {
    return `${(num / 1e6).toFixed(2)}M`
  } else if (num >= 1e3) {
    return `${(num / 1e3).toFixed(2)}K`
  }
  return num.toFixed(2)
}

const formatTimeAgo = (timestamp: number): string => {
  const now = Date.now() / 1000
  const diff = now - timestamp

  if (diff < 60) return `${Math.floor(diff)}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

const formatInterval = (seconds: number): string => {
  if (seconds < 60) return `${seconds.toFixed(0)}s`
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`
  return `${(seconds / 3600).toFixed(1)}h`
}

const formatCompactNumber = (value: number): string => {
  if (isNaN(value)) return '$0'
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(2)}B`
  } else if (value >= 1e6) {
    return `$${(value / 1e6).toFixed(2)}M`
  } else if (value >= 1e3) {
    return `$${(value / 1e3).toFixed(2)}K`
  }
  return `$${value.toFixed(2)}`
}

// Address naming utilities
const getAddressNames = (): { [address: string]: string } => {
  const stored = localStorage.getItem('bault-address-names')
  return stored ? JSON.parse(stored) : {}
}

const setAddressName = (address: string, name: string) => {
  const names = getAddressNames()
  names[address.toLowerCase()] = name
  localStorage.setItem('bault-address-names', JSON.stringify(names))
}

const getAddressDisplayName = (address: string): string => {
  const names = getAddressNames()
  const name = names[address.toLowerCase()]
  return name || `${address.slice(0, 6)}...${address.slice(-4)}`
}

// Component to get wrapper token symbol
const WrapperTokenSymbol: React.FC<{ wrapperAddress: string | null; rewardVaultRewardSymbol: string }> = ({
  wrapperAddress,
  rewardVaultRewardSymbol,
}) => {
  const token = useToken(wrapperAddress)

  if (!wrapperAddress) return <>{rewardVaultRewardSymbol}</>
  if (!token) return <>Wrapper Token</>

  return <>{token.symbol || 'Unknown'}</>
}

interface BaultCardItemProps {
  bault: any
  onSelect: () => void
  isExpanded: boolean
  onToggleExpand: () => void
  earnedData?: { earnedScaled: number | null; isLoading: boolean } | null
  completionData?: { completionPercentage: number | null; isLoading: boolean } | null
  performanceMetrics?: { rewardPerMinute: number | null; usdPerDollar: number | null } | null
  rewardVaultRewardToken?: Token | null
  isActive: boolean
  isLossMaking: boolean
}

type SortOption =
  | 'tvl'
  | 'apy'
  | 'avgInterval'
  | 'createdAt'
  | 'lastCompound'
  | 'earnedValue'
  | 'completionPercentage'
  | 'rewardPerMinute'
  | 'usdPerDollar'

const MIN_LOSS_USD = 0.01
const WRAPPER_REWARD_TOKEN_ADDRESS = '0xac03CABA51e17c86c921E1f6CBFBdC91F8BB2E6b'

// Custom hook to calculate total TVL using actual token prices
const useTotalTVL = (baultTVLs: Record<string, number>, baultCount: number) => {
  return useMemo(() => {
    if (baultCount === 0) return { usd: 0, hasUsdData: false, baultCount: 0, baultsWithPrices: 0 }

    let totalUsd = 0
    let baultsWithPrices = 0

    Object.values(baultTVLs).forEach((tvlUsd) => {
      if (tvlUsd && tvlUsd > 0) {
        totalUsd += tvlUsd
        baultsWithPrices++
      }
    })

    return {
      usd: totalUsd,
      hasUsdData: baultsWithPrices > 0,
      baultCount,
      baultsWithPrices,
    }
  }, [baultTVLs, baultCount])
}

const BaultCardItem: React.FC<BaultCardItemProps> = ({
  bault,
  onSelect,
  isExpanded,
  onToggleExpand,
  earnedData,
  completionData,
  performanceMetrics,
  rewardVaultRewardToken,
  isActive,
  isLossMaking,
}) => {
  const { chainId } = useConnection()
  const [editingAddress, setEditingAddress] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const rewardVaultRewardSymbol = rewardVaultRewardToken?.symbol || 'Reward'
  const rewardVaultRewardTokenAddress = rewardVaultRewardToken?.address

  // Get token price for USD conversion
  const { price: stakingTokenPrice } = useGetTokenPrice(bault.stakingToken.id)
  const { price: rewardTokenPrice } = useGetTokenPrice(rewardVaultRewardTokenAddress)
  const { price: wrapperRewardTokenPrice } = useGetTokenPrice(WRAPPER_REWARD_TOKEN_ADDRESS)

  // Use real-time earned value from the hook
  const earnedValue = earnedData?.earnedScaled || null

  // Calculate bounty value in USD (bounty * LP token price)
  const bountyValue = React.useMemo(() => {
    if (!stakingTokenPrice || !bault.bounty) return null
    const bountyAmount = parseFloat(bault.bounty) / Math.pow(10, bault.stakingToken.decimals)
    return bountyAmount * stakingTokenPrice
  }, [stakingTokenPrice, bault.bounty, bault.stakingToken.decimals])

  // Fetch compound events (always, not just when expanded)
  const { data: compoundData, isLoading: loadingCompounds } = useBaultCompounds({ id: bault.id, first: 10 })

  // Combine and sort direct and wrapper reward claims
  const allCompounds = React.useMemo(() => {
    if (!compoundData) return []

    const directClaims = compoundData.bgtClaims.map((claim) => ({
      ...claim,
      type: 'direct' as const,
    }))

    const wrapperClaims = compoundData.bgtWrapperClaims.map((claim) => ({
      ...claim,
      type: 'wrapper' as const,
    }))

    return [...directClaims, ...wrapperClaims]
      .sort((a, b) => parseInt(b.timestamp) - parseInt(a.timestamp))
      .slice(0, 10)
  }, [compoundData])

  // Calculate metrics
  const tvlUsd = stakingTokenPrice
    ? (parseFloat(bault.totalAssets) / Math.pow(10, bault.stakingToken.decimals)) * stakingTokenPrice
    : null
  const apy = formatPercentage(bault.apy)
  const compoundCount = bault.compoundCount || 0

  // Calculate average interval from last 5 compounds
  const avgInterval = React.useMemo(() => {
    if (!allCompounds || allCompounds.length < 2) return '0h'

    const last5 = allCompounds.slice(0, 5)
    const intervals = []

    for (let i = 0; i < last5.length - 1; i++) {
      const timeDiff = parseInt(last5[i].timestamp) - parseInt(last5[i + 1].timestamp)
      intervals.push(timeDiff)
    }

    if (intervals.length === 0) return '0h'
    const avgSeconds = intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length
    return formatInterval(avgSeconds)
  }, [allCompounds])

  const handleEditAddress = (address: string) => {
    setEditingAddress(address)
    setEditName(getAddressDisplayName(address))
  }

  const handleSaveAddress = () => {
    if (editingAddress && editName.trim()) {
      setAddressName(editingAddress, editName.trim())
    }
    setEditingAddress(null)
    setEditName('')
  }

  const handleCancelEdit = () => {
    setEditingAddress(null)
    setEditName('')
  }

  return (
    <>
      <BaultCard isExpanded={isExpanded} isLossMaking={isLossMaking}>
        <CardHeader onClick={onToggleExpand}>
          <BaultInfo>
            <BaultMeta>
              <BaultName>
                {bault.name} {isLossMaking && '⚠️'}
                {!isActive && <InactiveBadge>Inactive</InactiveBadge>}
              </BaultName>
              <BaultAddress>
                {bault.id.slice(0, 6)}...{bault.id.slice(-4)}
              </BaultAddress>
            </BaultMeta>
            <ExpandToggle>
              <span>{isExpanded ? 'Collapse' : 'Expand'}</span>
              {isExpanded ? <ChevronDown /> : <ChevronRight />}
            </ExpandToggle>
          </BaultInfo>

          <MetricsGrid>
            <MetricItem>
              <MetricLabel>TVL</MetricLabel>
              <MetricValue>
                {tvlUsd
                  ? formatCompactNumber(tvlUsd)
                  : `${formatTokenAmount(bault.totalAssets, bault.stakingToken.decimals)} ${bault.stakingToken.symbol}`}
              </MetricValue>
            </MetricItem>

            <MetricItem>
              <MetricLabel>APY</MetricLabel>
              <MetricValue>{apy}</MetricValue>
            </MetricItem>

            <MetricItem>
              <MetricLabel>Compounds</MetricLabel>
              <MetricValue>{compoundCount}</MetricValue>
            </MetricItem>

            <MetricItem>
              <MetricLabel>Avg Interval</MetricLabel>
              <MetricValue>{loadingCompounds ? '...' : avgInterval}</MetricValue>
            </MetricItem>

            <MetricItem>
              <MetricLabel>Earned Value (Scaled) {isLossMaking ? '⚠️' : ''}</MetricLabel>
              <MetricValue style={{ color: isLossMaking ? '#ff4444' : 'inherit' }}>
                {earnedData?.isLoading
                  ? 'Loading...'
                  : earnedValue && earnedValue > 0
                  ? formatCompactNumber(earnedValue)
                  : '$0.00'}
              </MetricValue>
            </MetricItem>

            <MetricItem>
              <MetricLabel>Bounty Value</MetricLabel>
              <MetricValue>
                {bountyValue && bountyValue > 0
                  ? formatCompactNumber(bountyValue)
                  : stakingTokenPrice
                  ? '$0.00'
                  : 'Loading...'}
              </MetricValue>
            </MetricItem>

            <MetricItem>
              <MetricLabel>Completion %</MetricLabel>
              <MetricValue
                style={{
                  color:
                    completionData?.completionPercentage && completionData.completionPercentage >= 80
                      ? '#4CAF50'
                      : completionData?.completionPercentage && completionData.completionPercentage >= 50
                      ? '#FF9800'
                      : '#757575',
                }}
              >
                {completionData?.isLoading
                  ? 'Loading...'
                  : completionData?.completionPercentage !== null && completionData?.completionPercentage !== undefined
                  ? `${completionData.completionPercentage.toFixed(1)}%`
                  : 'N/A'}
              </MetricValue>
            </MetricItem>

            <MetricItem>
              <MetricLabel>
                {rewardVaultRewardSymbol}/Min {bault.lastCompoundAt ? '(Recent)' : '(Overall)'}
              </MetricLabel>
              <MetricValue>
                {completionData?.isLoading
                  ? 'Loading...'
                  : performanceMetrics?.rewardPerMinute !== null && performanceMetrics?.rewardPerMinute !== undefined
                  ? performanceMetrics.rewardPerMinute.toFixed(6)
                  : 'N/A'}
              </MetricValue>
            </MetricItem>

            <MetricItem>
              <MetricLabel>USD/Day {bault.lastCompoundAt ? '(Recent)' : '(Overall)'}</MetricLabel>
              <MetricValue>
                {completionData?.isLoading
                  ? 'Loading...'
                  : performanceMetrics?.usdPerDollar !== null && performanceMetrics?.usdPerDollar !== undefined
                  ? formatCompactNumber(performanceMetrics.usdPerDollar)
                  : 'N/A'}
              </MetricValue>
            </MetricItem>
          </MetricsGrid>
        </CardHeader>

        {isExpanded && (
          <ExpandedContent>
            <ExpandedHeader>
              <ExpandedTitle>Recent Compounds</ExpandedTitle>
              <ViewDetailsButton onClick={onSelect}>View Detailed Analytics</ViewDetailsButton>
            </ExpandedHeader>

            <CompoundTable>
              <CompoundRow isHeader>
                <div>Timestamp</div>
                <div>Compounder</div>
                <div>Bounty</div>
                <div>Tokens</div>
                <div>PnL</div>
                <div>Tx</div>
              </CompoundRow>

              {loadingCompounds ? (
                <div style={{ textAlign: 'center', padding: '30px' }}>
                  <Loader />
                </div>
              ) : allCompounds && allCompounds.length > 0 ? (
                allCompounds.map((compound) => {
                  const bountyValue = (parseFloat(compound.compoundAmount) + parseFloat(compound.compoundFee)) / 1e18
                  const rewardTokenAmount =
                    compound.type === 'wrapper'
                      ? parseFloat(compound.wrapperTokenAmount) / 1e18
                      : parseFloat(compound.bgtAmount) / 1e18
                  const wrapperAddress = compound.type === 'wrapper' ? compound.bgtWrapperAddress.id : null

                  // Calculate USD values
                  const bountyUsd = stakingTokenPrice ? bountyValue * stakingTokenPrice : null
                  const rewardTokenUsd =
                    compound.type === 'wrapper'
                      ? wrapperRewardTokenPrice
                        ? rewardTokenAmount * wrapperRewardTokenPrice
                        : null
                      : rewardTokenPrice
                      ? rewardTokenAmount * rewardTokenPrice
                      : null

                  // Calculate leaked value (bounty USD - reward token USD)
                  const leakedValue = bountyUsd && rewardTokenUsd ? bountyUsd - rewardTokenUsd : null

                  return (
                    <CompoundRow key={compound.id}>
                      <CompoundCell className="timestamp">{formatTimeAgo(parseInt(compound.timestamp))}</CompoundCell>
                      <CompoundCell className="address">
                        {chainId ? (
                          <StyledInternalLink
                            to={getExplorerLink(chainId, compound.origin, ExplorerDataType.ADDRESS)}
                            target="_blank"
                            style={{ color: 'inherit' }}
                          >
                            {getAddressDisplayName(compound.origin)}
                          </StyledInternalLink>
                        ) : (
                          <span>{getAddressDisplayName(compound.origin)}</span>
                        )}
                        <EditIcon
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEditAddress(compound.origin)
                          }}
                        />
                      </CompoundCell>
                      <CompoundCell className="value">
                        <div>
                          {bountyValue.toFixed(4)} {bault.stakingToken.symbol}
                        </div>
                        {bountyUsd && <div className="usd">{formatCompactNumber(bountyUsd)}</div>}
                      </CompoundCell>
                      <CompoundCell className="value">
                        <div>
                          {rewardTokenAmount.toFixed(4)}{' '}
                          {compound.type === 'wrapper' ? (
                            <WrapperTokenSymbol
                              wrapperAddress={wrapperAddress}
                              rewardVaultRewardSymbol={rewardVaultRewardSymbol}
                            />
                          ) : (
                            rewardVaultRewardSymbol
                          )}
                        </div>
                        {rewardTokenUsd && <div className="usd">{formatCompactNumber(rewardTokenUsd)}</div>}
                      </CompoundCell>
                      <CompoundCell className={leakedValue && leakedValue < 0 ? 'leaked' : 'gained'}>
                        {leakedValue !== null ? formatCompactNumber(leakedValue) : '0'}
                      </CompoundCell>
                      <CompoundCell>
                        {compound.transactionHash && chainId ? (
                          <TxLink
                            href={getExplorerLink(chainId, compound.transactionHash, ExplorerDataType.TRANSACTION)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLink />
                          </TxLink>
                        ) : (
                          '-'
                        )}
                      </CompoundCell>
                    </CompoundRow>
                  )
                })
              ) : (
                <div style={{ textAlign: 'center', padding: '30px', color: 'inherit' }}>No compounds found</div>
              )}
            </CompoundTable>
          </ExpandedContent>
        )}
      </BaultCard>

      {editingAddress && (
        <>
          <AddressEditOverlay onClick={handleCancelEdit} />
          <AddressEditModal>
            <h4 style={{ margin: '0 0 10px 0', color: 'inherit' }}>Edit Address Name</h4>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>{editingAddress}</div>
            <AddressEditInput
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Enter display name"
              autoFocus
            />
            <AddressEditButtons>
              <AddressEditButton onClick={handleCancelEdit}>Cancel</AddressEditButton>
              <AddressEditButton primary onClick={handleSaveAddress}>
                Save
              </AddressEditButton>
            </AddressEditButtons>
          </AddressEditModal>
        </>
      )}
    </>
  )
}

export default function BaultAnalytics() {
  const [selectedBault, setSelectedBault] = useState<string | null>(null)
  const [expandedBaults, setExpandedBaults] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<SortOption>('tvl')
  const [showInactive, setShowInactive] = useState(false)

  const { data: baults, isLoading: isLoadingBaults, error: baultsError } = useAllBaults(DEFAULT_WAGMI_REFETCH_POLICY)
  const {
    data: islandsResponse,
    isLoading: isLoadingIslands,
    error: islandsError,
  } = useIslandsQuery({ withBaults: true, limit: 1000, orderBy: 'islandTvl' })
  const isLoading = isLoadingBaults || isLoadingIslands
  const error = baultsError || islandsError

  const activeBaultIds = useMemo(
    () =>
      new Set(
        islandsResponse?.data.flatMap((island) =>
          island.farm?.isActive ? island.baults.map((bault) => bault.id.toLowerCase()) : []
        )
      ),
    [islandsResponse]
  )
  const isBaultActive = useCallback((baultId: string) => activeBaultIds.has(baultId.toLowerCase()), [activeBaultIds])
  const inactiveCount = useMemo(
    () => baults?.filter((bault) => !isBaultActive(bault.id)).length ?? 0,
    [baults, isBaultActive]
  )

  const rewardVaultAddress = useMemo(() => baults?.find(Boolean)?.rewardVault, [baults])
  const { data: rewardVaultRewardTokenAddress } = useReadContract({
    address: rewardVaultAddress as Address | undefined,
    abi: REWARD_VAULT_ABI as Abi,
    functionName: 'rewardToken',
    query: {
      enabled: Boolean(rewardVaultAddress),
    },
  })

  const rewardVaultRewardToken = useToken(
    typeof rewardVaultRewardTokenAddress === 'string' ? rewardVaultRewardTokenAddress : undefined
  )
  const rewardVaultRewardSymbol = rewardVaultRewardToken?.symbol || 'Reward'

  const { price: rewardVaultRewardTokenPrice } = useGetTokenPrice(rewardVaultRewardToken?.address)

  // Get real-time earned values for all baults
  const baultAddresses = useMemo(() => baults?.map((b) => b.id), [baults])
  const earnedValues = useBaultEarnedValues(baultAddresses, rewardVaultRewardTokenPrice)

  // Create earned data lookup map
  const earnedDataMap = useMemo(() => {
    const map: Record<string, { earnedScaled: number | null; isLoading: boolean }> = {}
    earnedValues.forEach((data) => {
      map[data.address] = {
        earnedScaled: data.earnedInWrapper,
        isLoading: data.isLoading,
      }
    })
    return map
  }, [earnedValues])

  // Get all unique staking tokens for price fetching
  const uniqueTokens = useMemo(() => {
    if (!baults) return []
    return Array.from(new Set(baults.map((b) => b.stakingToken.id)))
  }, [baults])

  // Fetch all token prices at once with 10 second refresh
  const { prices: tokenPricesArray } = useGetTokenPrices(uniqueTokens)

  // Create price map and calculate TVLs
  const { tokenPricesMap, baultTVLs } = useMemo(() => {
    const priceMap: Record<string, number> = {}
    const tvls: Record<string, number> = {}

    if (tokenPricesArray && uniqueTokens) {
      uniqueTokens.forEach((tokenId, index) => {
        const price = tokenPricesArray[index]
        if (price && price > 0) {
          priceMap[tokenId.toLowerCase()] = price
        }
      })
    }

    if (baults) {
      baults.forEach((bault) => {
        const stakingTokenPrice = priceMap[bault.stakingToken.id.toLowerCase()]
        const { decimals } = bault.stakingToken
        if (stakingTokenPrice && stakingTokenPrice > 0 && decimals != null) {
          const tokenAmount = parseFloat(bault.totalAssets) / Math.pow(10, decimals)
          tvls[bault.id] = tokenAmount * stakingTokenPrice
        }
      })
    }

    return { tokenPricesMap: priceMap, baultTVLs: tvls }
  }, [baults, uniqueTokens, tokenPricesArray])

  // Calculate completion and performance metrics
  const completionMetrics = useBaultCompletionMetrics(baults || [], earnedDataMap, tokenPricesMap)

  // Create completion metrics lookup map
  const completionMetricsMap = useMemo(() => {
    const map: Record<
      string,
      {
        completionPercentage: number | null
        rewardPerMinute: number | null
        usdPerDollar: number | null
        isLoading: boolean
      }
    > = {}
    completionMetrics.forEach((data) => {
      map[data.address] = {
        completionPercentage: data.completionPercentage,
        rewardPerMinute: data.rewardPerMinute,
        usdPerDollar: data.usdPerDollar,
        isLoading: data.isLoading,
      }
    })
    return map
  }, [completionMetrics])

  // Use custom hook for total TVL calculation
  const totalTVL = useTotalTVL(baultTVLs, baults?.length || 0)

  // Add periodic refresh effect for real-time data
  useEffect(() => {
    const interval = setInterval(() => {
      // This will trigger a re-render and refresh of hooks that depend on current time
      // The actual data fetching is handled by the individual hooks with pollingInterval
    }, 10000) // 10 seconds

    return () => clearInterval(interval)
  }, [])

  const isBaultLossMaking = useCallback(
    (bault: NonNullable<typeof baults>[number]) => {
      const tvlUsd = baultTVLs[bault.id]
      const { decimals } = bault.stakingToken
      if (!tvlUsd || decimals == null) return false
      const stakingTokenPrice = tokenPricesMap[bault.stakingToken.id.toLowerCase()]
      const bountyUsd = (parseFloat(bault.bounty) / Math.pow(10, decimals)) * stakingTokenPrice
      const earnedUsd = earnedDataMap[bault.id]?.earnedScaled || 0
      return earnedUsd - bountyUsd > MIN_LOSS_USD
    },
    [baultTVLs, tokenPricesMap, earnedDataMap]
  )

  // Filter and sort baults
  const filteredAndSortedBaults = useMemo(() => {
    if (!baults) return []

    // Filter by search term - support flexible searching
    const filtered = baults.filter((bault) => {
      if (!showInactive && !isBaultActive(bault.id)) return false
      if (!searchTerm.trim()) return true

      const searchLower = searchTerm.toLowerCase().trim()
      const nameMatch = bault.name.toLowerCase().includes(searchLower)
      const nameNoHyphen = bault.name
        .toLowerCase()
        .replace(/[-_\s]/g, '')
        .includes(searchLower.replace(/[-_\s]/g, ''))
      const addressMatch = bault.id.toLowerCase().includes(searchLower)
      const symbolMatch = !!bault.stakingToken.symbol?.toLowerCase().includes(searchLower)

      return nameMatch || nameNoHyphen || addressMatch || symbolMatch
    })

    // Sort baults
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'tvl': {
          // Sort by USD TVL value
          const aTVL = baultTVLs[a.id] || 0
          const bTVL = baultTVLs[b.id] || 0
          return bTVL - aTVL
        }
        case 'apy':
          return parseFloat(b.apy) - parseFloat(a.apy)
        case 'avgInterval': {
          const aInterval = a.averageCompoundInterval ? parseInt(a.averageCompoundInterval) : Infinity
          const bInterval = b.averageCompoundInterval ? parseInt(b.averageCompoundInterval) : Infinity
          return aInterval - bInterval
        }
        case 'createdAt':
          return parseInt(b.createdAt) - parseInt(a.createdAt)
        case 'lastCompound': {
          const aLast = a.lastCompoundAt ? parseInt(a.lastCompoundAt) : 0
          const bLast = b.lastCompoundAt ? parseInt(b.lastCompoundAt) : 0
          return bLast - aLast
        }
        case 'earnedValue': {
          // Sort by real-time earned values
          const aEarned = earnedDataMap[a.id]?.earnedScaled || 0
          const bEarned = earnedDataMap[b.id]?.earnedScaled || 0
          return bEarned - aEarned
        }
        case 'completionPercentage': {
          // Sort by completion percentage
          const aCompletion = completionMetricsMap[a.id]?.completionPercentage || 0
          const bCompletion = completionMetricsMap[b.id]?.completionPercentage || 0
          return bCompletion - aCompletion
        }
        case 'rewardPerMinute': {
          // Sort by reward earned per minute
          const aRewardPerMin = completionMetricsMap[a.id]?.rewardPerMinute || 0
          const bRewardPerMin = completionMetricsMap[b.id]?.rewardPerMinute || 0
          return bRewardPerMin - aRewardPerMin
        }
        case 'usdPerDollar': {
          // Sort by USD per dollar efficiency
          const aUsdPerDollar = completionMetricsMap[a.id]?.usdPerDollar || 0
          const bUsdPerDollar = completionMetricsMap[b.id]?.usdPerDollar || 0
          return bUsdPerDollar - aUsdPerDollar
        }
        default:
          return 0
      }
    })

    // Sort loss-making baults to the top (these need attention)
    const lossMaking: any[] = []
    const regular: any[] = []

    filtered.forEach((bault) => {
      if (isBaultLossMaking(bault)) {
        lossMaking.push(bault)
      } else {
        regular.push(bault)
      }
    })

    return [...lossMaking, ...regular]
  }, [
    baults,
    searchTerm,
    sortBy,
    earnedDataMap,
    baultTVLs,
    completionMetricsMap,
    showInactive,
    isBaultActive,
    isBaultLossMaking,
  ])

  // Count loss-making baults
  const lossMakingCount = useMemo(() => baults?.filter(isBaultLossMaking).length ?? 0, [baults, isBaultLossMaking])

  const selectBault = (baultId: string) => {
    setSelectedBault(baultId)
  }

  const goBackToList = () => {
    setSelectedBault(null)
  }

  const toggleExpand = (baultId: string) => {
    const newExpanded = new Set(expandedBaults)
    if (newExpanded.has(baultId)) {
      newExpanded.delete(baultId)
    } else {
      newExpanded.add(baultId)
    }
    setExpandedBaults(newExpanded)
  }

  // Detailed view for selected bault
  if (selectedBault) {
    const selectedBaultData = baults?.find((b) => b.id === selectedBault)

    return (
      <PageContainer>
        <BackButton onClick={goBackToList}>← Back to Bault List</BackButton>
        <Header>
          <Title>{selectedBaultData?.name} - Detailed Analytics</Title>
          <Subtitle>{selectedBault}</Subtitle>
        </Header>

        <BaultView
          baultId={selectedBault}
          pool={undefined}
          timeframe="30"
          rewardVaultRewardToken={rewardVaultRewardToken}
        />
      </PageContainer>
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <Header>
          <Title>Bault Analytics</Title>
          <Subtitle>Loading baults...</Subtitle>
          <TotalTVLContainer>
            <TotalTVLLabel>Total Value Locked (All Baults)</TotalTVLLabel>
            <TotalTVLValue>Loading...</TotalTVLValue>
          </TotalTVLContainer>
        </Header>
        <LoadingContainer>
          <Loader />
        </LoadingContainer>
      </PageContainer>
    )
  }

  // Error state
  if (error || !baults || baults.length === 0) {
    return (
      <PageContainer>
        <Header>
          <Title>Bault Analytics</Title>
          <Subtitle>{error ? 'Error loading baults' : 'No baults found'}</Subtitle>
        </Header>
        <ErrorContainer>{error ? 'Please try again later.' : 'No baults are currently available.'}</ErrorContainer>
      </PageContainer>
    )
  }

  // List view
  return (
    <PageContainer>
      <Header>
        <Title>Bault Analytics</Title>
        <Subtitle>
          Monitor bault performance metrics ({filteredAndSortedBaults.length} of {baults.length} baults shown)
        </Subtitle>

        <TotalTVLContainer>
          <TotalTVLLabel>Total Value Locked (All Baults)</TotalTVLLabel>
          <TotalTVLValue>
            {totalTVL.hasUsdData && totalTVL.usd > 0
              ? `${formatCompactNumber(totalTVL.usd)}`
              : `${totalTVL.baultCount} Active Baults`}
          </TotalTVLValue>
        </TotalTVLContainer>

        <ControlsContainer>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Search baults by name, symbol, or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <SearchIcon />
          </SearchContainer>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            {lossMakingCount > 0 && (
              <LossMakingBadge title="These baults are paying out more in earned rewards than they receive in bounties">
                ⚠️ {lossMakingCount} Loss-Making Bault{lossMakingCount !== 1 ? 's' : ''} (Action Needed)
              </LossMakingBadge>
            )}

            <SortContainer as="label">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              <SortLabel>Show inactive ({inactiveCount})</SortLabel>
            </SortContainer>

            <SortContainer>
              <SortLabel>Sort by:</SortLabel>
              <SortSelect value={sortBy} onChange={(e) => setSortBy(e.target.value as SortOption)}>
                <option value="tvl">TVL (Highest)</option>
                <option value="apy">APY (Highest)</option>
                <option value="avgInterval">Compound Interval (Fastest)</option>
                <option value="createdAt">Creation Date (Newest)</option>
                <option value="lastCompound">Last Compound (Latest)</option>
                <option value="earnedValue">Loss Risk (Highest Loss First)</option>
                <option value="completionPercentage">Completion % (Highest)</option>
                <option value="rewardPerMinute">{rewardVaultRewardSymbol}/Min Recent (Highest)</option>
                <option value="usdPerDollar">USD/Day Recent (Highest)</option>
              </SortSelect>
            </SortContainer>
          </div>
        </ControlsContainer>
      </Header>

      <BaultList>
        {filteredAndSortedBaults.map((bault) => (
          <BaultCardItem
            key={bault.id}
            bault={bault}
            onSelect={() => selectBault(bault.id)}
            isExpanded={expandedBaults.has(bault.id)}
            onToggleExpand={() => toggleExpand(bault.id)}
            earnedData={earnedDataMap[bault.id]}
            completionData={
              completionMetricsMap[bault.id]
                ? {
                    completionPercentage: completionMetricsMap[bault.id].completionPercentage,
                    isLoading: completionMetricsMap[bault.id].isLoading,
                  }
                : null
            }
            performanceMetrics={completionMetricsMap[bault.id]}
            isActive={isBaultActive(bault.id)}
            isLossMaking={isBaultLossMaking(bault)}
            rewardVaultRewardToken={rewardVaultRewardToken}
          />
        ))}
      </BaultList>
    </PageContainer>
  )
}
